/**
 * Pure math for the photo viewers' zoom / pan / rotate (L-1212). No React, no DOM: every rule is pinned by a plain test.
 *
 * The photo's transform is `translate(pan) rotate(r) scale(z)` about the photo's own centre, so a screen point p maps from a photo
 * point q as  p = c + pan + R·S·(q − c)  (c = the photo box centre when unpanned). Zooming about the pointer keeps the photo point
 * under the pointer fixed: pan' = (p − c) − (p − c − pan)·(z'/z).
 */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 8;
/** Button / key step: kept at the viewers' long-standing 0.5. */
export const ZOOM_STEP = 0.5;
/** Mouse-wheel sensitivity: one 100px notch ≈ 15%. */
const WHEEL_SENSITIVITY = 0.0015;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export const ORIGIN: Point = { x: 0, y: 0 };

export function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** The zoom after one wheel event (deltaY > 0 = away from the user = zoom out). */
export function wheelZoom(zoom: number, deltaY: number, deltaMode = 0): number {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY; // lines / pages -> pixels
  return clampZoom(zoom * Math.exp(-px * WHEEL_SENSITIVITY));
}

/**
 * New pan after changing zoom from `from` to `to` with the pointer at `pointer` (screen px) and the photo centred on `centre`.
 * At zoom <= 1 the photo is centred again (nothing to pan to).
 */
export function zoomAtPoint(pan: Point, from: number, to: number, pointer: Point, centre: Point): Point {
  if (to <= 1) return ORIGIN;
  const ratio = to / from;
  const px = pointer.x - centre.x;
  const py = pointer.y - centre.y;
  return { x: px - (px - pan.x) * ratio, y: py - (py - pan.y) * ratio };
}

/**
 * The CSS transform. With no pan it is EXACTLY the pre-L-1212 string (`rotate(90deg) scale(1.5)`), so nothing that read it changes.
 */
export function viewTransform(rotation: number, zoom: number, pan: Point): string {
  const base = `rotate(${rotation}deg) scale(${zoom})`;
  return pan.x === 0 && pan.y === 0 ? base : `translate(${Math.round(pan.x * 100) / 100}px, ${Math.round(pan.y * 100) / 100}px) ${base}`;
}

/** Zoom that shows the photo at its real pixel size, given its fitted on-screen width at zoom 1 (null = unknown). */
export function actualSizeZoom(naturalWidth: number, fittedWidth: number | null): number | null {
  if (!fittedWidth || fittedWidth <= 0 || naturalWidth <= 0) return null;
  return clampZoom(naturalWidth / fittedWidth);
}

/** The zoom as a percentage of the photo's real size ("100%" = one photo pixel per screen pixel); null when unknown. */
export function percentOfActual(zoom: number, naturalWidth: number | null, fittedWidth: number | null): number | null {
  if (!naturalWidth || !fittedWidth || naturalWidth <= 0 || fittedWidth <= 0) return null;
  return Math.round(zoom * (fittedWidth / naturalWidth) * 100);
}

/**
 * Keeps a panned photo from being dragged out of reach: its centre may travel at most half the viewing area times the zoom
 * (so at 2x you can reach every edge of the photo, and a stray drag can never leave it entirely off screen). An unmeasured
 * area (0 x 0) is left alone; Fit always recovers.
 */
export function clampPan(pan: Point, zoom: number, areaW: number, areaH: number): Point {
  if (!(areaW > 0) || !(areaH > 0)) return pan;
  const maxX = (areaW / 2) * zoom;
  const maxY = (areaH / 2) * zoom;
  return { x: Math.min(maxX, Math.max(-maxX, pan.x)), y: Math.min(maxY, Math.max(-maxY, pan.y)) };
}
